// src/pages/VacationBalance/VacationBalanceHistoryPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import dayjs from 'dayjs';

import api from '../../services/api';
import {
  DataTableShell,
  EmptyState,
  FilterBar,
  PageHeader,
  PageLayout,
  SectionCard,
  StatCard,
} from '../../components/ui';

function VacationBalanceHistoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentYear = dayjs().year();
  const defaultDateFrom = `${currentYear}-01-01`;
  const defaultDateTo = `${currentYear}-12-31`;
  const debounceTimer = useRef(null);
  const initialQuery = useRef(null);

  if (!initialQuery.current) {
    const params = new URLSearchParams(location.search);
    initialQuery.current = {
      userId: params.get('user_id') || '',
      from: params.get('from') || defaultDateFrom,
      to: params.get('to') || defaultDateTo,
    };
  }

  const [userId, setUserId] = useState(initialQuery.current.userId);
  const [dateFrom, setDateFrom] = useState(initialQuery.current.from);
  const [dateTo, setDateTo] = useState(initialQuery.current.to);
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const fetchHistory = useCallback(async (targetUserId, from, to) => {
    try {
      const response = await api.get('/admin/vacation-balances2/history2', {
        params: { user_id: targetUserId, from, to },
      });
      setHistoryData(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setError('Could not fetch history. Please try again.');
      setHistoryData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const query = new URLSearchParams();
    if (userId) query.set('user_id', userId);
    if (dateFrom) query.set('from', dateFrom);
    if (dateTo) query.set('to', dateTo);

    const nextUrl = `${location.pathname}${query.toString() ? `?${query.toString()}` : ''}`;
    if (window.history.replaceState) {
      window.history.replaceState(null, '', nextUrl);
    }
  }, [dateFrom, dateTo, location.pathname, userId]);

  useEffect(() => {
    if (!userId) {
      setHistoryData([]);
      setError('');
      setLoading(false);
      return undefined;
    }

    if (!isValidRange(dateFrom, dateTo)) {
      setError('Invalid date range: "From" must be before or equal to "To".');
      setHistoryData([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError('');

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchHistory(userId, dateFrom, dateTo);
    }, 500);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [dateFrom, dateTo, fetchHistory, userId]);

  const stats = useMemo(() => {
    let increased = 0;
    let decreased = 0;

    historyData.forEach((entry) => {
      const change = toNum(entry.balance_after) - toNum(entry.balance_before);
      if (change > 0) increased += change;
      if (change < 0) decreased += Math.abs(change);
    });

    return {
      total: historyData.length,
      increased,
      decreased,
      net: increased - decreased,
    };
  }, [historyData]);

  const handleSearchImmediate = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!userId) {
      setHistoryData([]);
      setError('');
      setLoading(false);
      return;
    }
    if (!isValidRange(dateFrom, dateTo)) {
      setError('Invalid date range: "From" must be before or equal to "To".');
      return;
    }
    setLoading(true);
    setError('');
    fetchHistory(userId, dateFrom, dateTo);
  }, [dateFrom, dateTo, fetchHistory, userId]);

  const setQuickRange = (key) => {
    const today = dayjs();
    if (key === 'this_month') {
      setDateFrom(today.startOf('month').format('YYYY-MM-DD'));
      setDateTo(today.endOf('month').format('YYYY-MM-DD'));
    } else if (key === 'last_30') {
      setDateFrom(today.subtract(29, 'day').format('YYYY-MM-DD'));
      setDateTo(today.format('YYYY-MM-DD'));
    } else if (key === 'this_year') {
      setDateFrom(today.startOf('year').format('YYYY-MM-DD'));
      setDateTo(today.endOf('year').format('YYYY-MM-DD'));
    } else if (key === 'last_year') {
      const lastYear = today.year() - 1;
      setDateFrom(dayjs(`${lastYear}-01-01`).format('YYYY-MM-DD'));
      setDateTo(dayjs(`${lastYear}-12-31`).format('YYYY-MM-DD'));
    }
  };

  const resetFilters = () => {
    setUserId(initialQuery.current.userId || '');
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
  };

  const exportCsv = () => {
    if (!historyData.length) return;

    const header = [
      'Leave Type',
      'Balance Before',
      'Balance After',
      'Change',
      'Changed By',
      'Reason',
      'Date Changed',
    ];
    const rows = historyData.map((entry) => {
      const change = toNum(entry.balance_after) - toNum(entry.balance_before);
      return [
        safe(entry.leave_type),
        toNum(entry.balance_before),
        toNum(entry.balance_after),
        change,
        safe(entry?.changed_by?.name || '-'),
        safe(entry.reason),
        entry.created_at ? dayjs(entry.created_at).format('YYYY-MM-DD HH:mm') : '-',
      ];
    });

    const csv = [header, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${userId ? `history_${userId}` : 'history'}_${dateFrom}_${dateTo}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    setOkMsg('CSV exported.');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setOkMsg('Link copied to clipboard.');
    } catch {
      setOkMsg('Unable to copy link.');
    }
  };

  const rangeInvalid = Boolean(dateFrom && dateTo && !isValidRange(dateFrom, dateTo));
  const emptyState = !userId ? (
    <EmptyState
      compact
      icon={PersonOutlineIcon}
      title="Enter an employee code"
      description="Type an employee code to load vacation balance changes for the selected date range."
    />
  ) : !historyData.length ? (
    <EmptyState
      compact
      icon={SwapHorizIcon}
      title="No history entries"
      description="Try a wider date range or confirm the employee code."
      actionLabel="Use current year"
      onAction={() => setQuickRange('this_year')}
    />
  ) : null;

  const desktopTable = (
    <TableContainer>
      <Table stickyHeader size="small" aria-label="Vacation balance history">
        <TableHead>
          <TableRow>
            <TableCell>Leave Type</TableCell>
            <TableCell>Before</TableCell>
            <TableCell>After</TableCell>
            <TableCell>Change</TableCell>
            <TableCell>Changed By</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Date Changed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {historyData.map((entry) => (
            <HistoryRow key={entry.id || `${entry.leave_type}-${entry.created_at}`} entry={entry} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const mobileCards = (
    <Stack spacing={1.1}>
      {historyData.map((entry) => (
        <HistoryCard key={entry.id || `${entry.leave_type}-${entry.created_at}`} entry={entry} />
      ))}
    </Stack>
  );

  return (
    <PageLayout maxWidth="xl" backgroundAccent>
      <PageHeader
        variant="dashboard"
        overline="Vacation balance history"
        title="Balance audit trail"
        subtitle="Track every leave balance change by employee, date range, reason, and administrator."
        actions={
          <>
            <Button variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/admin/vacation-balances')}>
              Balances
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleSearchImmediate} disabled={loading || !userId}>
              Refresh
            </Button>
          </>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gap: 1.4,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          mb: 1.6,
        }}
      >
        <StatCard
          tone="dashboard"
          icon={<SwapHorizIcon fontSize="small" />}
          title="Changes"
          value={stats.total}
          hint={userId ? `Employee ${userId}` : 'Waiting for employee code'}
          color="primary.main"
        />
        <StatCard
          tone="dashboard"
          icon={<TrendingUpIcon fontSize="small" />}
          title="Increased"
          value={`${round2(stats.increased)}d`}
          hint="Added balance"
          color="success.main"
        />
        <StatCard
          tone="dashboard"
          icon={<TrendingDownIcon fontSize="small" />}
          title="Decreased"
          value={`${round2(stats.decreased)}d`}
          hint="Used or reduced balance"
          color="error.main"
        />
        <StatCard
          tone="dashboard"
          icon={<CalendarMonthIcon fontSize="small" />}
          title="Net movement"
          value={`${round2(stats.net)}d`}
          hint={`${dateFrom} to ${dateTo}`}
          color="warning.main"
        />
      </Box>

      <SectionCard
        tone="dashboard"
        title="Search history"
        subheader="Enter an employee code and choose the date range. Results refresh automatically after a short pause."
        sx={{ mb: 1.4 }}
      >
        <FilterBar
          dense
          onReset={resetFilters}
          left={
            <>
              <TextField
                label="Employee Code"
                placeholder="e.g. 10462"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
                size="small"
                sx={{ minWidth: { xs: '100%', sm: 220 } }}
                InputProps={{ startAdornment: <PersonOutlineIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
              <TextField
                label="From"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', sm: 170 } }}
              />
              <TextField
                label="To"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { xs: '100%', sm: 170 } }}
              />
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearchImmediate}>
                Search
              </Button>
            </>
          }
          right={
            <>
              <Tooltip title="Export CSV">
                <span>
                  <IconButton onClick={exportCsv} disabled={!historyData.length} aria-label="Export CSV">
                    <FileDownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Copy link to this search">
                <span>
                  <IconButton onClick={copyLink} aria-label="Copy link">
                    <ContentCopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          }
        />

        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
          <Button size="small" onClick={() => setQuickRange('this_month')} startIcon={<CalendarMonthIcon />}>
            This Month
          </Button>
          <Button size="small" onClick={() => setQuickRange('last_30')} startIcon={<CalendarMonthIcon />}>
            Last 30 Days
          </Button>
          <Button size="small" onClick={() => setQuickRange('this_year')} startIcon={<CalendarMonthIcon />}>
            This Year
          </Button>
          <Button size="small" onClick={() => setQuickRange('last_year')} startIcon={<CalendarMonthIcon />}>
            Last Year
          </Button>
          <Button size="small" onClick={resetFilters} startIcon={<RestartAltIcon />}>
            Reset
          </Button>
        </Stack>

        {!userId ? (
          <Alert severity="info" sx={{ mt: 1.2 }}>
            Enter an employee code to load vacation balance history.
          </Alert>
        ) : null}
        {rangeInvalid ? (
          <Alert severity="warning" sx={{ mt: 1.2 }}>
            Date range is invalid. Ensure From is before or equal to To.
          </Alert>
        ) : null}
      </SectionCard>

      <DataTableShell
        title="History entries"
        subtitle={
          userId
            ? `Showing vacation balance changes for employee ${userId} from ${dateFrom} to ${dateTo}.`
            : 'Search by employee code to view vacation balance changes.'
        }
        loading={loading}
        error={error}
        onRetry={handleSearchImmediate}
        empty={emptyState}
        mobileCards={mobileCards}
        tone="dashboard"
      >
        {desktopTable}
      </DataTableShell>

      <Snackbar
        open={Boolean(okMsg)}
        autoHideDuration={2500}
        onClose={() => setOkMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setOkMsg('')} variant="filled">
          {okMsg}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}

function HistoryRow({ entry }) {
  const before = toNum(entry.balance_before);
  const after = toNum(entry.balance_after);
  const change = after - before;

  return (
    <TableRow hover>
      <TableCell>{entry.leave_type || '-'}</TableCell>
      <TableCell>{round2(before)}</TableCell>
      <TableCell>{round2(after)}</TableCell>
      <TableCell>
        <ChangeChip value={change} />
      </TableCell>
      <TableCell>{entry?.changed_by?.name || '-'}</TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <Typography variant="body2" noWrap title={entry.reason || '-'}>
          {entry.reason || '-'}
        </Typography>
      </TableCell>
      <TableCell>{entry.created_at ? dayjs(entry.created_at).format('YYYY-MM-DD HH:mm') : '-'}</TableCell>
    </TableRow>
  );
}

function HistoryCard({ entry }) {
  const theme = useTheme();
  const before = toNum(entry.balance_before);
  const after = toNum(entry.balance_after);
  const change = after - before;

  return (
    <Box
      sx={{
        p: 1.45,
        borderRadius: theme.hr?.radius?.md || 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: theme.hr?.surface?.glass || 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 850 }} noWrap>
            {entry.leave_type || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {entry.created_at ? dayjs(entry.created_at).format('YYYY-MM-DD HH:mm') : '-'}
          </Typography>
        </Box>
        <ChangeChip value={change} />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 1,
          mt: 1.2,
          p: 1,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.04),
        }}
      >
        <Metric label="Before" value={round2(before)} />
        <Metric label="After" value={round2(after)} />
      </Box>

      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.1 }}>
        Changed by
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {entry?.changed_by?.name || '-'}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.8 }}>
        Reason
      </Typography>
      <Typography variant="body2">{entry.reason || '-'}</Typography>
    </Box>
  );
}

function Metric({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
        {value}
      </Typography>
    </Box>
  );
}

function ChangeChip({ value }) {
  const isIncrease = value > 0;
  const isDecrease = value < 0;

  return (
    <Chip
      size="small"
      color={isIncrease ? 'success' : isDecrease ? 'error' : 'default'}
      icon={isIncrease ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : isDecrease ? <TrendingDownIcon sx={{ fontSize: 16 }} /> : undefined}
      label={`${value > 0 ? '+' : ''}${round2(value)} d`}
      variant={value === 0 ? 'outlined' : 'filled'}
      sx={{ fontWeight: 800 }}
    />
  );
}

function isValidRange(from, to) {
  if (!from || !to) return false;
  const start = dayjs(from);
  const end = dayjs(to);
  return start.isValid() && end.isValid() && (start.isBefore(end) || start.isSame(end, 'day'));
}

function toNum(value) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

function round2(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function safe(value) {
  return (value ?? '').toString().replace(/\r?\n|\r/g, ' ').trim();
}

function csvEscape(value) {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export default VacationBalanceHistoryPage;
